import { DOCUMENT } from '@angular/common';
import {
    AfterViewInit,
    Component,
    EventEmitter,
    HostListener,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

import { Variant } from '@ap/amounts';
import { ApContentService, ApHeadlessContentService } from '@ap/content';
import { ApHeaderComponent, NavigationItem, NavigationType, ProfileMenu, ProfileMenuItem } from '@ap/header';
import { KlantbeheerResource } from '@ap/interface-apic-klantbeheer';
import { ContractDetails } from '@ap/interface-apix-mijn-overzicht';
import { MijnConditiesService } from '@ap/mijn-condities';
import { MijnFeedbackSettings, Variants } from '@ap/mijn-feedback';
import { MijnLaunchDarklyService } from '@ap/mijn-kanaalconfiguratie';
import { MijnNavigatieService, MijnRouteKey } from '@ap/mijn-navigatie';
import { ApModalComponent, ApModalService, ModalClosedReason, ModalOptions, ModalSize } from '@ap/modal';
import { ApPersistenceService } from '@ap/persistence';

import { MijnOverzichtTemplateService } from '../mijn-overzicht-template';
import { ChatNotification } from '../mijn-overzicht-template/conversational/conversational.model';
import {
    CONVERSATIONAL_CONFIGURATION_NAME,
    MAX_BUTTON_TEXTLENGTH_FOR_ROW_LAYOUT
} from '../mijn-overzicht-template/mijn-overzicht-template.constant';
import { FeedbackConfiguration, MijnFeedbackForm } from '../mijn-overzicht-template/mijn-overzicht-template.model';
import { SendableOrdersModalComponent } from '../mijn-overzicht-template/sendable-orders-modal/sendable-orders-modal.component';
import content from '../mijn-template.content.json';
import { MijnTemplateNavigation } from '../mijn-template.navigation';
import { MijnTemplateService } from '../mijn-template.service';

import { Breadcrumb } from './breadcrumb/breadcrumb.model';
import { PEGA_HEADLESS_WARNING } from './pega-overzicht-template.constant';
import { PegaOverzichtTemplateService } from './pega-overzicht-template.service';

/**
 * @module MijnOverzichtTemplateModule
 * @ngdoc component
 * @name MijnOverzichtTemplateComponent
 */
@Component({
    selector: 'pega-overzicht-template',
    templateUrl: './pega-overzicht-template.component.html',
    styleUrls: ['./pega-overzicht-template.component.scss'],
    standalone: false
})
export class PegaOverzichtTemplateComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
    @ViewChild(ApHeaderComponent)
    public header: ApHeaderComponent;

    @Input()
    public title: string;

    @Input()
    public breadcrumb: Breadcrumb;

    @Input()
    public subtitle?: string;

    @Input()
    public secondarySubtitle?: string;

    @Input()
    public amount?: number;

    @Input()
    public amountVariant: Variant = Variant.NEGATIVE_ACCENT;

    @Input()
    public amountContrast = false;

    @Input()
    public secondarySubAmount?: number;

    @Input()
    public primaryActionLabel?: string;

    @Input()
    public secondaryActionLabel?: string;

    @Input()
    public variant: 'primary' | 'secondary' = 'primary';

    @Input()
    public loading: boolean;

    @Input()
    public avatar?: string;

    @Input()
    public changeConversationalStatus$: Subject<string>;

    /**
     * Updates the dynamic breadcrumb and should be used when a router-outlet is used inside the template
     */
    @Input()
    public updateURL?: string;

    @Input()
    public feedbackConfiguration?: FeedbackConfiguration;

    @Input()
    public updateUnreadMessages$: Subject<void>;

    @Output()
    public primaryActionClicked = new EventEmitter<void>();

    @Output()
    public secondaryActionClicked = new EventEmitter<void>();

    @Output()
    public chatNotificationSent = new EventEmitter<ChatNotification>();

    public navigationItems: NavigationItem[] = [];
    public profileMenu: ProfileMenu;
    public logoLink: string;
    public secondaryClassList: string;
    public dynamicBreadcrumb: Breadcrumb;
    public conversationalEnabled: boolean;
    public reload = false;
    public businessEnabled: boolean;
    public buttonsInColumns = false;

    public pegaTemplateWarning: string;
    public usabillaFormSettings: MijnFeedbackForm = null;
    public skipLinkTarget: HTMLElement;

    private modalComponent: ApModalComponent;
    private unreadMessagesSize = 0;
    private userName = null;
    private companyName = null;
    private contractDetails: ContractDetails;
    private readonly subscriptions = new Subscription();

    public constructor(
        private readonly contentService: ApContentService,
        @Inject(DOCUMENT) private readonly document: Document,
        private readonly apHeadlessContentService: ApHeadlessContentService,
        private readonly navigation: MijnTemplateNavigation,
        private readonly mijnNavigatieService: MijnNavigatieService,
        private readonly launchdarklyService: MijnLaunchDarklyService,
        private readonly pegaOverzichtService: PegaOverzichtTemplateService,
        private readonly modalService: ApModalService,
        private readonly mijnOverzichtService: MijnOverzichtTemplateService,
        private readonly persistenceService: ApPersistenceService,
        private readonly klantbeheerResource: KlantbeheerResource,
        private readonly renderer: Renderer2,
        private readonly mijnConditiesService: MijnConditiesService
    ) {}

    public get mijnFeedbackSettings(): MijnFeedbackSettings {
        return {
            ...this.usabillaFormSettings,
            ...this.feedbackConfiguration,
            type: Variants.MOOD_SIDEBAR
        };
    }

    @HostListener('window:popstate')
    public onPopState(): void {
        if (this.modalComponent) {
            this.modalComponent.cancel();
        }
    }

    public ngOnInit(): void {
        this.contentService.addContentItems(content);
        this.logoLink = this.getLogoLink();
        this.navigationItems = this.getNavigationItems();
        this.setButtonOrientation();

        this.subscriptions.add(
            this.mijnConditiesService.initializeConditionParameters().subscribe(() => {
                this.processHeader();
            })
        );

        this.subscriptions.add(
            this.klantbeheerResource.geefIngelogdeKlantDetails().subscribe((result) => {
                this.userName = result.combinedName;
                this.companyName = result.handelsnaam;

                this.processHeader();
            })
        );

        this.subscriptions.add(
            this.pegaOverzichtService.getNrOfUnreadMessages().subscribe((unreadMessages) => {
                this.unreadMessagesSize = unreadMessages;

                this.processHeader();
            })
        );

        this.apHeadlessContentService
            .getContentItems(PEGA_HEADLESS_WARNING)
            .pipe(map((result) => MijnTemplateService.mapHeadlessContentItems(result)))
            .subscribe((headlessContent) => {
                if (headlessContent) {
                    this.contentService.addHeadlessContentItems(headlessContent);
                    this.pegaTemplateWarning = this.getPegaTemplateWarning();
                }
            });

        this.subscriptions.add(
            this.pegaOverzichtService.getContractDetails().subscribe((contractDetails) => {
                this.contractDetails = contractDetails;

                this.logoLink = this.getLogoLink();
                this.navigationItems = this.getNavigationItems();
            })
        );

        this.subscriptions.add(
            this.launchdarklyService.getFlagValue(CONVERSATIONAL_CONFIGURATION_NAME, false).subscribe((conversationalEnabled) => {
                this.conversationalEnabled = conversationalEnabled && this.chatNotificationSent.observed;
            })
        );

        this.pegaOverzichtService.loadNrOfUnreadMessages();

        if (this.updateUnreadMessages$) {
            this.subscriptions.add(
                this.updateUnreadMessages$.subscribe(() => {
                    this.pegaOverzichtService.loadNrOfUnreadMessages();
                })
            );
        }

        if (!this.updateURL && !this.breadcrumb) {
            this.pegaOverzichtService.storeUrl(null, this.title);
        }

        this.setupFeedback();
    }

    public ngAfterViewInit() {
        this.skipLinkTarget = this.document.getElementById('main-content');
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (this.contractDetails != null) {
            this.logoLink = this.getLogoLink();
            this.navigationItems = this.getNavigationItems();
        }
        if (this.variant === 'secondary') {
            this.renderer.addClass(this.document.body, 'mijn-overzicht-template__body--secondary');
        } else {
            this.renderer.removeClass(this.document.body, 'mijn-overzicht-template__body--secondary');
        }
        this.secondaryClassList = this.setClasses(this.variant);

        if ((changes.updateURL || changes.breadcrumb || changes.title) && changes.title?.currentValue !== '') {
            this.initBreadcrumb(this.updateURL, this.title);
        }
        if (changes.secondaryActionLabel && !this.loading) {
            this.setButtonOrientation();
        }
        if (changes.feedbackConfiguration) {
            this.setupFeedback();
        }
    }

    public setClasses(variant: string): string {
        if (variant === 'secondary') {
            return 'mijn-overzicht-template' + ' ' + 'mijn-overzicht-template--secondary';
        }
        return 'mijn-overzicht-template';
    }

    public onNavigation(item: NavigationItem): void {
        if (item.type === NavigationType.LINK_SAME_TAB) {
            this.navigation.external(item.link);
        }
    }

    public onProfileItemClicked(item: ProfileMenuItem) {
        if (item.routeKey) {
            this.mijnNavigatieService.navigate(MijnRouteKey[item.routeKey]);
        } else if (item.link) {
            window.location.href = item.link;
            window.location.reload();
        } else {
            console.error('No link target provided!'); //NOSONAR
        }
    }

    public onLoginLogout(logout: boolean) {
        if (!logout) {
            return;
        }

        this.subscriptions.add(
            this.pegaOverzichtService.getNrOfSendableOrders().subscribe((result) => {
                result === 0 ? this.logout() : this.openSendableOrdersModal();
            })
        );
    }

    public ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        this.renderer.removeClass(this.document.body, 'mijn-overzicht-template__body--secondary');
    }

    public hasActions(): boolean {
        return !!this.primaryActionLabel || !!this.secondaryActionLabel;
    }

    public doPrimaryAction(): void {
        this.primaryActionClicked.emit();
    }

    public doSecondaryAction(): void {
        this.secondaryActionClicked.emit();
    }

    public toContact(): void {
        this.navigation.external(this.getContextualLink('mijn.overzicht.template.header.contact.'));
    }

    private getPegaTemplateWarning(): string {
        const warning = this.contentService.get('pega.template.warning');
        if (warning === null || warning.length === 0 || warning.indexOf('pega.template.warning') === 0) {
            return undefined;
        }
        return warning;
    }

    private setupFeedback(): void {
        if (this.feedbackConfiguration) {
            this.subscriptions.add(
                this.pegaOverzichtService.getMijnFeedbackSettings(this.feedbackConfiguration.configurationKey).subscribe((result) => {
                    this.usabillaFormSettings = result;
                })
            );
        }
    }

    private getNavigationItems(): NavigationItem[] {
        return [this.getOverviewItem(), this.getSelfserviceItem(), this.getContactItem()];
    }

    private getLogoLink(): string {
        return this.getContextualLink('mijn.overzicht.template.header.logo.');
    }

    private getOverviewItem(): NavigationItem {
        return {
            label: this.contentService.get('mijn.overzicht.template.header.overview.title'),
            link: this.getContextualLink('mijn.overzicht.template.header.overview.'),
            type: NavigationType.LINK_SAME_TAB
        };
    }

    private getSelfserviceItem(): NavigationItem {
        return {
            label: this.contentService.get('mijn.overzicht.template.header.mijnKlantZelfservice.title'),
            link: this.getContextualLink('mijn.overzicht.template.header.mijnKlantZelfservice.'),
            type: NavigationType.LINK_SAME_TAB
        };
    }

    private getContactItem(): NavigationItem {
        const singleOrPlural = this.unreadMessagesSize === 1 ? 'single' : 'multiple';
        const ariaLabelKey = `mijn.overzicht.template.header.contact.ariaLabel.${singleOrPlural}`;
        const ariaLabelText = `${this.contentService.get('mijn.overzicht.template.header.contact.title')}, ${
            this.unreadMessagesSize
        } ${this.contentService.get(ariaLabelKey)}`;
        return {
            label: this.contentService.get('mijn.overzicht.template.header.contact.title'),
            ariaLabel: ariaLabelText,
            link: this.getContextualLink('mijn.overzicht.template.header.contact.'),
            type: NavigationType.LINK_SAME_TAB,
            badge: this.unreadMessagesSize
        };
    }

    private getTargetLabel(): string {
        if (this.contractDetails?.is_zakelijk) {
            return 'business';
        }
        return 'private';
    }

    private logout(): void {
        const logoutUrl = this.contentService.get(`mijn.overzicht.template.header.logout.${this.getTargetLabel()}.url`);
        this.pegaOverzichtService.clearDynamicBreadcrumbs();
        this.pegaOverzichtService.clearNBASession();
        this.pegaOverzichtService.terminateUserSession().subscribe({
            next: () => {
                this.persistenceService.clearSession('mijn-conversational.chat-session');
                this.navigation.external(logoutUrl);
            }
        });
    }

    private openSendableOrdersModal(): void {
        const modalOptions = new ModalOptions();
        modalOptions.title = this.contentService.get('mijn.overzicht.template.sendableOrdersModal.title');
        modalOptions.size = ModalSize.MEDIUM;

        this.modalComponent = this.modalService.open(SendableOrdersModalComponent, modalOptions);
        this.subscriptions.add(
            this.modalComponent.afterClosed().subscribe((result) => {
                this.handleModalClosedReason(result.reason);
            })
        );
    }

    private handleModalClosedReason(reason: ModalClosedReason): void {
        if (reason === ModalClosedReason.ACCEPTED) {
            this.logout();
        } else if (reason === ModalClosedReason.DECLINED) {
            this.navigation.external(this.contentService.get('mijn.overzicht.template.verzendlijst.url'));
        }
    }

    private initBreadcrumb(url?: string, title?: string): void {
        this.pegaOverzichtService.storeUrl(url, title);
        this.dynamicBreadcrumb = this.pegaOverzichtService.getDynamicBreadcrumb() ?? this.breadcrumb;
    }

    private getContextualLink(contentPrefix: string, contentSuffix = '.url'): string {
        return this.contentService.get(`${contentPrefix}${this.getTargetLabel()}${contentSuffix}`);
    }

    private setButtonOrientation(): void {
        if (this.secondaryActionLabel != null) {
            this.buttonsInColumns =
                this.primaryActionLabel.length + this.secondaryActionLabel.length > MAX_BUTTON_TEXTLENGTH_FOR_ROW_LAYOUT;
        }
    }

    private processHeader() {
        this.profileMenu = this.mijnOverzichtService.setupProfileHeader(this.userName, this.companyName, this.unreadMessagesSize);

        this.ngOnChanges({});
    }
}
